import { useCallback, useEffect, useState } from 'react'
import { api } from '../lib/api'
import { useApp } from '../store/AppContext'

/**
 * Two-factor enrolment.
 *
 * Both the QR code and the typed secret are shown, because plenty of people
 * scan and plenty cannot — a phone without a camera, a desktop authenticator,
 * a password manager. Offering only the QR quietly excludes them.
 *
 * Enrolment is not finished until a code is verified. Supabase keeps the
 * factor in 'unverified' until then, which is right: a factor nobody has
 * proved they can generate codes for is not protection, it is a lockout
 * waiting to happen.
 */
export function MfaPanel() {
  const { flash, refreshAuth } = useApp()

  const [enrolled, setEnrolled] = useState<{ id: string; friendlyName: string | null }[]>([])
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState(false)
  const [pending, setPending] = useState<{ factorId: string; qr: string; secret: string } | null>(
    null,
  )
  const [code, setCode] = useState('')
  const [err, setErr] = useState('')
  const [showSecret, setShowSecret] = useState(false)

  const reload = useCallback(async () => {
    try {
      const factors = await api.mfa.listVerified()
      setEnrolled(factors.map((f) => ({ id: f.id, friendlyName: f.friendly_name ?? null })))
    } catch {
      setEnrolled([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const begin = async () => {
    setStarting(true)
    setErr('')
    try {
      setPending(await api.mfa.enroll())
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not start enrolment.')
    } finally {
      setStarting(false)
    }
  }

  const finish = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!pending) return
    try {
      await api.mfa.verify(pending.factorId, code)
      setPending(null)
      setCode('')
      setErr('')
      await reload()
      /*
       * Re-read the session before anything else touches the database.
       *
       * The factor is verified the instant that call returns, and the
       * database now refuses a session that holds a factor it has not used.
       * Verifying raises this session to aal2 and hands back a new token --
       * but the app is still holding the old one until it looks again, and
       * carrying on with it would blank every screen the moment enrolment
       * succeeded. If anything did go wrong, this lands on the code prompt,
       * which is the recoverable place to be.
       */
      await refreshAuth()
      flash('Two-factor authentication is on')
    } catch (problem) {
      setErr(problem instanceof Error ? problem.message : 'That code was not accepted.')
    }
  }

  const turnOff = async (factorId: string) => {
    try {
      await api.mfa.unenroll(factorId)
      await reload()
      await refreshAuth()
      flash('Two-factor authentication is off')
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Could not turn it off.')
    }
  }

  if (loading) return null

  return (
    <div className="card card--pad">
      <div className="card-title">Two-factor authentication</div>
      <p className="pretty" style={{ fontSize: 12.5, color: 'var(--mut)', margin: '4px 0 14px' }}>
        A six-digit code from an authenticator app, on top of your password. Anyone who learns your
        password still cannot sign in without your phone.
      </p>

      {enrolled.length > 0 && !pending && (
        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <span className="status status--on">On</span>
          <span style={{ fontSize: 12.5, color: 'var(--mut)', flex: 1, minWidth: 0 }}>
            You’ll be asked for a code each time you sign in.
          </span>
          <button
            type="button"
            className="linkbtn"
            style={{ color: 'var(--dg)' }}
            onClick={() => void turnOff(enrolled[0].id)}
          >
            Turn off
          </button>
        </div>
      )}

      {enrolled.length === 0 && !pending && (
        <button type="button" className="btn btn--primary" disabled={starting} onClick={() => void begin()}>
          {starting ? 'Starting…' : 'Turn on two-factor'}
        </button>
      )}

      {pending && (
        <form onSubmit={(e) => void finish(e)}>
          <p style={{ fontSize: 13, margin: '0 0 12px' }}>
            Scan this with Google Authenticator, 1Password, Authy or similar, then type the code it
            shows.
          </p>

          {/* Supabase returns the QR as an SVG data URI. */}
          <img
            src={pending.qr}
            alt="QR code for setting up two-factor authentication"
            width={180}
            height={180}
            style={{ background: '#fff', padding: 8, borderRadius: 10, display: 'block' }}
          />

          <button
            type="button"
            className="linkbtn"
            style={{ color: 'var(--mut)', margin: '10px 0' }}
            onClick={() => setShowSecret((v) => !v)}
          >
            {showSecret ? 'Hide code' : 'Can’t scan? Type it instead'}
          </button>

          {showSecret && (
            <code
              className="mono"
              style={{
                display: 'block',
                fontSize: 13,
                padding: '8px 10px',
                marginBottom: 12,
                borderRadius: 8,
                background: 'var(--canvas)',
                border: '1px solid var(--line)',
                wordBreak: 'break-all',
              }}
            >
              {pending.secret}
            </code>
          )}

          <label className="label" htmlFor="mfa-code">
            Code from the app
          </label>
          <input
            id="mfa-code"
            className="input mono"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={7}
            style={{ maxWidth: 180, fontSize: 18, letterSpacing: '.18em', textAlign: 'center' }}
            value={code}
            onChange={(e) => {
              setCode(e.target.value)
              setErr('')
            }}
            placeholder="000000"
          />

          {err && (
            <p style={{ color: 'var(--dg)', fontSize: 13, margin: '10px 0 0' }} role="alert">
              {err}
            </p>
          )}

          <div className="row" style={{ gap: 8, marginTop: 14 }}>
            <button type="submit" className="btn btn--primary">
              Confirm and turn on
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setPending(null)
                setCode('')
                setErr('')
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {err && !pending && (
        <p style={{ color: 'var(--dg)', fontSize: 13, margin: '10px 0 0' }} role="alert">
          {err}
        </p>
      )}
    </div>
  )
}
